/**
 * When a session's log stream cannot open for an expected reason, the drawer says so calmly
 * instead of raising a red error: its machine is offline or asleep (409), or the server has no
 * such route or no longer holds the log (404). What Orgus kept from the session record still
 * shows. Anything else stays an error. Pure, so it is tested.
 */
export type StreamNote = 'offline' | 'unavailable';

function statusOf(e: unknown): number | null {
  if (!e || typeof e !== 'object') return null;
  const o = e as { status?: unknown; ui?: { status?: unknown } };
  const v = typeof o.status === 'number' ? o.status : typeof o.ui?.status === 'number' ? o.ui.status : null;
  return v;
}

/** The calm note for a stream error, or null when it is a real error. `machineOnline`: the fleet's view, when known. */
export function streamNoteOf(e: unknown, machineOnline?: boolean): StreamNote | null {
  if (!e) return null;
  const status = statusOf(e);
  if (status === 409 || machineOnline === false) return 'offline';
  if (status === 404) return 'unavailable';
  return null;
}

/** The note's sentence: what is shown and why, with no blame on the person. */
export function streamNoteText(note: StreamNote, machineName?: string): string {
  const who = machineName || 'The machine';
  return note === 'offline'
    ? `${who} is offline or asleep. This is what Orgus kept from the session; the full transcript streams again when the machine is back.`
    : 'The full transcript is not available here. This is what Orgus kept from the session.';
}
